/**
 * The lot card (from the Stitch export, 1:1). One component, two shapes:
 *  - with a result from the first model: marketed price, verdict and maximum
 *    bid on the photo, consensus dots for every selected model; address first
 *    in the body;
 *  - without: the price is the first line of the body.
 * The strip is a stacked ledger: one row per selected model with the model's
 * own headline figures and a bid bar against the marketed price. Which tiles
 * appear comes from the active experience spec. A saved result that is not the
 * latest calculation carries the stale mark beside its verdict.
 */
import { useState, type MouseEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { IconAlertTriangle, IconBookmark, IconBookmarkFilled, IconCalendar, IconCheck, IconChevronDown, IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import type { CardItem, SelectedDealModel } from '../../api/types'
import { effectivePropertyClassification, propertyClassificationLabel } from '../../api/propertyClassification'
import { area, beds, dateShort, humanize, money, moneyExact, photoVariant } from '../../api/format'
import { useListsForProperty, useSetListMembership } from '../../api/queries'
import { TILE_LABELS, type ExperienceSpec, type TileKey } from '../../experiences/registry'
import { Popover, Verdict } from '../../ui/kit'
import { StaleMark } from '../../ui/StaleMark'
import { bidFraction, maxBidPence, modelFigures, modelStatus, staleOf, verdictOf } from './modelOutputs'

export { verdictOf } from './modelOutputs'

export function tileClass(score: number | null): string {
  if (score === null) return 'na'
  if (score < 20) return 'low'
  if (score < 55) return 'mid'
  return 'good'
}

export function scoreFor(item: CardItem, key: TileKey) {
  const d = item.diligence
  const s = key === 'resale' ? d?.resalePotential : key === 'rent' ? d?.rentalPotential : key === 'area' ? d?.areaRisk : d?.familyFit
  if (!s || s.score === null || s.score === undefined) return { value: null as number | null, partial: false }
  return { value: Math.round(s.score), partial: s.state === 'partial' || (s.assumedWeightPercent ?? 0) > 0 }
}

/** One ledger row per selected model: the result, its verdict, its own figures and its bid. */
export function modelRows(item: CardItem, models: SelectedDealModel[], guide: number | null) {
  return models.map(model => {
    const result = item.dealsV2Results.find(r => r.logicalModelId === model.logicalModelId)
    const verdict = verdictOf(result)
    const maxBid = maxBidPence(result)
    return { model, result, verdict, maxBid, stale: staleOf(result), fraction: bidFraction(maxBid, guide), figures: modelFigures(model, result), status: modelStatus(result) }
  })
}

export function LotCard({ item, models, spec, listId, compact }: { item: CardItem; models: SelectedDealModel[]; spec: ExperienceSpec; listId?: string; compact?: boolean }) {
  const photos = (item.photoUrls?.length ? item.photoUrls : item.card?.listing?.coverUrl ? [item.card.listing.coverUrl] : [])
  const [index, setIndex] = useState(0)
  const photo = photos.length ? photos[index % photos.length] : undefined
  const move = (event: MouseEvent, direction: 1 | -1) => { event.preventDefault(); event.stopPropagation(); setIndex(i => (i + direction + photos.length) % photos.length) }

  const p = item.card?.property
  const listing = item.card?.listing
  const address = [p?.address?.line1, p?.address?.city].filter(Boolean).join(', ') || 'Address unavailable'
  const postcode = p?.address?.postcode ?? ''
  const guide = listing?.marketedPricePence ?? null
  const rows = modelRows(item, models, guide)
  const primary = rows[0]
  const auction = dateShort(listing?.auctionDate)
  const classification = effectivePropertyClassification(p ?? {})
  const facts = [propertyClassificationLabel(classification, true), humanize(classification.builtForm), p?.tenure ? humanize(p.tenure) + (item.card?.lease?.statedYearsRemaining ? ` (${item.card.lease.statedYearsRemaining} yrs)` : '') : null, beds(classification.bedrooms), area(item.card?.valuation?.floorAreaSqm, item.card?.valuation?.floorAreaAssumed)].filter(Boolean).join(' • ')
  const hasResult = Boolean(primary?.verdict && primary.maxBid !== null)
  const status = listing?.listingStatus ?? item.propertyStatus
  const anyLow = spec.card.tiles.some(key => { const s = scoreFor(item, key); return s.value !== null && s.value < 20 })
  const to = { to: '/properties/$propertyId' as const, params: { propertyId: item.propertyId }, search: listId ? { listId } : {} }
  const dots = rows.length > 1 ? <span className="cdots" aria-label={rows.map(r => `${r.model.modelName ?? 'Model'}: ${r.verdict?.label ?? r.status}${r.stale.stale ? ' (not latest)' : ''}`).join(', ')}>{rows.map(r => <i key={r.model.logicalModelId} className={`cdot ${r.verdict?.cls ?? 'na'}`} />)}</span> : null

  return (
    <article className={compact ? 'card is-compact' : 'card'}>
      <header className="auction-header">
        <IconCalendar className="ic-sm" aria-hidden="true" /><span>Auction date</span>
        {auction ? <time dateTime={listing!.auctionDate!}>{auction}</time> : <span className="auction-unknown">Not provided</span>}
      </header>
      <Link {...to} className="hero" aria-label={`Open ${address}`}>
        {photo ? <><div className="img bg" key={`bg:${photo}`} style={{ backgroundImage: `url(${photoVariant(photo, 'small')})` }} /><div className="img" key={photo} style={{ backgroundImage: `url(${photoVariant(photo, 'small')})` }} /></> : null}
        <div className="grad" />
        <div className="tl"><span className={`badge ${status === 'withdrawn' || status === 'archived' ? 'off' : 'live'}`}>{humanize(status)}</span></div>
        {photos.length ? <span className="count">{(index % photos.length) + 1} / {photos.length}</span> : null}
        {photos.length > 1 ? <>
          <button type="button" className="nav-btn prev" onClick={e => move(e, -1)} aria-label="Previous photo"><IconChevronLeft className="ic" /></button>
          <button type="button" className="nav-btn next" onClick={e => move(e, 1)} aria-label="Next photo"><IconChevronRight className="ic" /></button>
        </> : null}
        {hasResult && spec.card.photoStrip === 'bid' ? (
          <div className="micro"><div className="mp"><span className="lab">Marketed</span><span className="val">{money(guide)}</span><Verdict band={primary!.verdict!.cls}>{primary!.verdict!.label}</Verdict>{primary!.stale.stale ? <StaleMark compact reasons={primary!.stale.reasons} /> : null}</div><div className="mx">{dots}<span className="lab"><span className="mname">{primary!.model.modelName ?? 'Model'} </span>max: </span><span className={`val ${primary!.verdict!.cls}`}>{moneyExact(primary!.maxBid)}</span></div></div>
        ) : (
          <div className="micro"><span className="st"><span className="dot" />{hasResult ? `${primary!.verdict!.label} · ${primary!.model.modelName ?? 'Model'}` : 'Marketed price • Unresolved'}{hasResult && primary!.stale.stale ? <StaleMark compact reasons={primary!.stale.reasons} /> : null}</span><span className="pc">{dots}{postcode}</span></div>
        )}
      </Link>
      <div className="body">
        <div>
          {hasResult ? (
            <div className="ap"><Link {...to} className="addr">{address}</Link><span className="pc">{postcode}</span></div>
          ) : (
            <div className="price-row"><span className="price">{money(guide)}</span><span className="price-lab">{listing?.auctionDate ? 'Guide Price' : 'Asking Price'}</span></div>
          )}
          {hasResult ? null : <Link {...to} className="addr">{address}</Link>}
          <p className={['facts', hasResult ? 'b' : ''].join(' ')}>{facts}</p>
        </div>
        <div className="tiles" aria-label="Evidence scores">
          {spec.card.tiles.map(key => {
            const s = scoreFor(item, key)
            let cls = tileClass(s.value)
            if (cls === 'good' && anyLow && s.value !== null && s.value >= 80) cls = 'top'
            return <div className={`tile ${cls}`} key={key}><div className="l">{TILE_LABELS[key]}</div><div className="v">{s.partial ? <IconAlertTriangle className="ic-sm" aria-label="Partial evidence" /> : null}{s.value ?? '—'}</div></div>
          })}
        </div>
        {spec.card.strip === 'none' ? null : rows.length === 0 ? (
          <div className="strip"><div className="row"><span className="lft"><span className="m">Models</span><span className="gd" /><span>No model selected</span></span><Link {...to} className="chev" aria-label="Open"><IconChevronRight className="ic" /></Link></div></div>
        ) : (
          <div className="strip b ledger" aria-label="Model results">
            {rows.map(r => (
              <div className="mrow" key={r.model.logicalModelId}>
                <div className="row">
                  <div className="lft"><span className="m">{r.model.modelName ?? 'Model'}{r.result?.bid?.profile && r.verdict ? `: ${humanize(r.result.bid.profile)}` : ''}</span>{r.verdict ? <Verdict band={r.verdict.cls}>{r.verdict.label}</Verdict> : <Verdict band="na">{r.status}</Verdict>}{r.stale.stale ? <StaleMark interactive reasons={r.stale.reasons} /> : null}</div>
                  {r.verdict ? <div className="rgt"><span className="amt" title={r.figures.h1Label}>{r.figures.h1}</span>{r.figures.h2 ? <span className="pct" title={r.figures.h2Label}>• {r.figures.h2}</span> : null}</div> : <Link {...to} className="chev" aria-label="Open"><IconChevronRight className="ic" /></Link>}
                </div>
                <div className={r.fraction === null ? 'bar is-none' : 'bar'} title={r.maxBid !== null ? `${r.model.modelName ?? 'Model'} max bid ${moneyExact(r.maxBid)} vs ${money(guide)}` : 'No maximum bid from this model'}>{r.fraction !== null ? <i className={r.verdict?.cls ?? 'na'} style={{ width: `${r.fraction.toFixed(0)}%` }} /> : null}</div>
              </div>
            ))}
          </div>
        )}
        <CardFoot item={item} />
      </div>
    </article>
  )
}

/** Visited is recorded when you open the lot; it is shown, never toggled by hand. */
export function VisitedTag({ visited }: { visited: boolean | undefined }) {
  return visited ? <span className="vtag" title="You have opened this lot"><IconCheck className="ic-sm" aria-hidden="true" />Visited</span> : null
}

function CardFoot({ item }: { item: CardItem }) {
  const membership = useSetListMembership()
  const [open, setOpen] = useState(false)
  const personal = item.personalSettings ?? null
  const savedCount = item.savedListIds?.length ?? 0
  return (
    <div className="foot">
      <span className="src" title={item.card?.listing?.source ?? ''}>{item.card?.listing?.source ?? '—'}</span>
      <div className="acts">
        <VisitedTag visited={personal?.visited} />
        <Popover open={open} onClose={() => setOpen(false)} align="end" width={240} anchor={
          <button type="button" className={['btn', 'is-small', savedCount ? 'is-saved' : ''].join(' ')} onClick={() => setOpen(o => !o)} aria-haspopup="dialog" aria-expanded={open} title={savedCount ? `In ${savedCount} ${savedCount === 1 ? 'list' : 'lists'}` : 'Save to a list'}>
            {savedCount ? <IconBookmarkFilled className="ic-sm" aria-hidden="true" /> : <IconBookmark className="ic-sm" aria-hidden="true" />}{savedCount ? 'Saved' : 'Save'} <IconChevronDown className="ic-sm" aria-hidden="true" />
          </button>
        }>
          <div className="pop-title">Save to a list</div>
          <ListMembership propertyId={item.propertyId} busy={membership.isPending} onToggle={(listId, member) => membership.mutate({ listId, propertyId: item.propertyId, member })} />
        </Popover>
      </div>
    </div>
  )
}

/** Membership rows for one lot; the lists endpoint takes ?propertyId to mark membership. */
function ListMembership({ propertyId, busy, onToggle }: { propertyId: string; busy: boolean; onToggle: (listId: string, member: boolean) => void }) {
  const lists = useListsForProperty(propertyId)
  if (lists.isPending) return <span className="t-muted">Loading lists…</span>
  if (lists.isError) return <span className="t-muted">{lists.error.message}</span>
  return (
    <div className="pop-list">
      {lists.data.length === 0 ? <span className="t-muted">No lists yet. Create one on the Lists page.</span> : null}
      {lists.data.map(list => (
        <button type="button" key={list.id} className={list.containsProperty ? 'is-on' : ''} disabled={busy} onClick={() => onToggle(list.id, !list.containsProperty)}>
          <span>{list.name}</span><span className="count">{list.propertyCount}{list.containsProperty ? <IconCheck className="ic-sm" aria-hidden="true" /> : null}</span>
        </button>
      ))}
    </div>
  )
}
